/**
 * PremiumCelebration — Full-screen overlay shown when Premium is activated.
 * Previously inline in App.tsx (lines 963-1015).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GraduationCap, Infinity, Bot, Brain, ShieldOff } from 'lucide-react';

interface PremiumCelebrationProps {
  onClose: () => void;
}

const PremiumCelebration: React.FC<PremiumCelebrationProps> = ({ onClose }) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ animation: 'fadeIn 0.3s ease' }}
    >
      <div
        className="glass-strong rounded-3xl max-w-sm w-full mx-4 p-8 text-center"
        style={{ animation: 'scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
      >
        <div className="relative mb-4">
          <div style={{ fontSize: 64, animation: 'bounce 0.6s ease infinite alternate' }}>🎉</div>
          <div
            className="absolute -top-2 left-1/4"
            style={{ fontSize: 28, animation: 'confetti1 1s ease infinite' }}
          >
            ✨
          </div>
          <div
            className="absolute -top-1 right-1/4"
            style={{ fontSize: 24, animation: 'confetti2 1.2s ease infinite' }}
          >
            🌟
          </div>
          <div
            className="absolute top-0 left-1/6"
            style={{ fontSize: 20, animation: 'confetti3 0.8s ease infinite' }}
          >
            🎊
          </div>
          <div
            className="absolute top-2 right-1/6"
            style={{ fontSize: 22, animation: 'confetti1 1.1s ease infinite' }}
          >
            💫
          </div>
        </div>

        <h2
          className="text-2xl font-black mb-2 dark:text-white"
          style={{
            background: 'linear-gradient(135deg, #FFD700, #FF8C00)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('Premium aktiviert! Vielen Dank. 🎉')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
          {t('premium_celebration_desc')}
        </p>

        <div className="space-y-3 mb-6 text-left">
          {[
            { Icon: GraduationCap, text: t('premium_feature_unlock_all') },
            { Icon: Infinity, text: t('premium_feature_unlimited_sims') },
            { Icon: Bot, text: t('premium_feature_ai_explanations') },
            { Icon: Brain, text: t('premium_feature_smart_learning') },
            { Icon: ShieldOff, text: t('premium_feature_ad_free') },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-100 dark:border-amber-800/30"
              style={{ animation: `slideInRight ${0.3 + i * 0.1}s ease backwards` }}
            >
              <item.Icon size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {item.text}
              </span>
              <Check size={16} className="ml-auto text-green-500" strokeWidth={3} />
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full py-3.5 rounded-2xl font-bold text-white text-base"
          style={{
            background: 'linear-gradient(135deg, #FFD700, #FF8C00)',
            boxShadow: '0 4px 15px rgba(255,140,0,0.4)',
          }}
        >
          {t('premium_celebration_cta')}
        </button>
      </div>

      <style>{`
        @keyframes scaleIn { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(30px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes confetti1 { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-15px) rotate(15deg); } }
        @keyframes confetti2 { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-20px) rotate(-10deg); } }
        @keyframes confetti3 { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-12px) scale(1.2); } }
      `}</style>
    </div>
  );
};

export default PremiumCelebration;
